import { AccountingSheetInterface, CompareDetails } from '@/types/AccountingSheet';
import { Pretension } from '@/types/Pretension';
import { accountingSheet } from '@/utils/config';
import PretensionController from '@/controllers/Pretension';
import {
  Vue,
  Component,
} from 'vue-property-decorator';

type DocumentDisposition = 'inline' | 'attachment';

/**
 * Общая часть документа листа учёта и документа претензии: ссылку и подпись плитки
 * строим по тем полям, что есть у обоих. Вердикт сверки приходит только у листа.
 */
type DocumentLinkSource = {
  bucket: string;
  objectName: string;
  type: string | null;
  compareResult?: string | null;
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  rejectComment?: string | null;
  acceptedAt?: string | null;
  acceptedBy?: string | null;
  compareDetails?: CompareDetails | null;
}

export type DownloadDocumentsInterface = {
  link: string;
  type: string | undefined;
  name: string | undefined;
  /**
   * BC3.3 followup (rev.4): disposition определяется по расширению objectName.
   * Backend имеет final authority (выставляет Content-Disposition header в response),
   * frontend использует это только для UI решения о target=_blank.
   */
  disposition: DocumentDisposition;
  /**
   * BC3.3 followup (rev.4): null → Vue 2 опускает атрибут полностью (no target в DOM).
   * '_blank' → открыть в новой вкладке (для inline PDF preview).
   */
  target: '_blank' | null;
  /**
   * Исход сверки файла поставщика с нашей версией листа (identical / content_equal /
   * differs). null — документ сверку не проходил, подсветки у плитки нет.
   */
  compareResult: string | null;
  /** Ключ S3 и тип строки как пришли с бэка: по ним снимают отказ от файла поставщика. */
  objectName: string;
  documentType: string | null;
  /** Отметка отказа от файла поставщика, время в UTC. null — файл не отклоняли. */
  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectComment: string | null;
  /** Согласие с изменённым файлом поставщика, время в UTC. null — согласия нет. */
  acceptedAt: string | null;
  acceptedBy: string | null;
  /** Расхождения файла поставщика с нашим листом. null — показывать нечего. */
  compareDetails: CompareDetails | null;
}

@Component
export default class DocumentsMixin extends Vue {
  private documents: DownloadDocumentsInterface[] = [];

  public get downloadDocuments(): DownloadDocumentsInterface[] {
    return this.documents;
  }

  private getNameFromS3ObjectName(objectName: string): string {
    const splitted = objectName.split('/');
    return splitted[splitted.length - 1];
  }

  public getDocumentType(type: string | null): string {
    const map = new Map([
      ['sheet', 'Лист'],
      // Версия листа, присланная поставщиком: та же форма документа, что и sheet,
      // отличается подсветкой плитки по исходу сверки.
      ['sheet_supplier', 'Лист'],
      ['sheet_archive', 'ZIP'],
      ['act', 'Акт'],
      ['upd', 'УПД'],
      ['account', 'Счет'],
      ['invoice', 'Счет-фактура'],
      ['reconciliation_act', 'Акт сверки'],
      // Документ документооборота Диадока, который не является формализованным:
      // счёт на оплату, приложение поставщика. Подпись короткая намеренно — в
      // плитку «Неформализованный документ» не помещается, а бухгалтеру важно
      // ровно то, что это не УПД.
      ['nonformalized', 'Документ'],
      ['default', 'Doc'],
    ]);
    const name = (type !== null ? map.get(type) : undefined) ?? map.get('default');
    return name || '';
  }

  /**
   * Подсветка плитки документа по исходу сверки файла поставщика с нашей версией листа.
   * Цвет отдаётся hex'ом: Vuetify ставит его inline-стилем, поэтому фон карточки из темы
   * подсветку не перебивает. Документы, сверку не проходившие, цвета не получают.
   */
  public compareColor(compareResult: string | null): string | undefined {
    const colors = new Map([
      ['identical', '#C8E6C9'],
      ['content_equal', '#FFECB3'],
      ['differs', '#FFCDD2'],
    ]);
    return compareResult !== null ? colors.get(compareResult) : undefined;
  }

  /**
   * Расшифровка цвета для нативной подсказки: без неё цветная плитка ничего не объясняет.
   */
  public compareHint(compareResult: string | null): string | undefined {
    const hints = new Map([
      ['identical', 'Файл поставщика совпадает с нашим листом'],
      ['content_equal', 'Данные совпадают, файл оформлен иначе'],
      ['differs', 'Есть расхождения с нашим листом'],
    ]);
    return compareResult !== null ? hints.get(compareResult) : undefined;
  }

  /** Отклонённый файл поставщика бледнеет поверх цвета сверки: соглашаться с ним уже не будут. */
  public documentColor(document: DownloadDocumentsInterface): string | undefined {
    return document.rejectedAt ? '#EEEEEE' : this.compareColor(document.compareResult);
  }

  /**
   * Красная плитка остаётся красной и после согласия — расхождение бухгалтерии полезно
   * видеть; подсказка добавляет, кто и когда изменения принял.
   */
  public documentHint(document: DownloadDocumentsInterface): string | undefined {
    if (document.rejectedAt) {
      return this.rejectionHint(document);
    }
    const hint = this.compareHint(document.compareResult);
    if (!hint || !document.acceptedAt) {
      return hint;
    }
    const accepted = [document.acceptedBy, this.moscowTime(document.acceptedAt)].filter(Boolean).join(', ');
    return `${hint}. Изменения приняты: ${accepted}`;
  }

  /** «Отклонен 15.09.2026, 10:04, Иванова Анна (HR): причина». */
  public rejectionHint(document: Pick<DownloadDocumentsInterface, 'rejectedAt' | 'rejectedBy' | 'rejectComment'>): string {
    const by = document.rejectedBy ? `, ${document.rejectedBy}` : '';
    const reason = document.rejectComment ? `: ${document.rejectComment}` : '';
    return `Отклонен ${this.moscowTime(document.rejectedAt)}${by}${reason}`;
  }

  /** Время с бэка в UTC («2026-09-15 07:04:05»), показываем МСК. Не разобрали — как пришло. */
  public moscowTime(utcTime: string | null): string | null {
    const utc = new Date(`${(utcTime || '').replace(' ', 'T')}Z`);
    return Number.isNaN(utc.getTime()) ? utcTime : utc.toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }

  /**
   * BC3.3 followup (rev.4): зеркало backend whitelist'а — .pdf only inline.
   * Public для testability; private для frontend invariant.
   */
  private inferDispositionFromObjectName(objectName: string): DocumentDisposition {
    const ext = objectName.split('.').pop()?.toLowerCase() ?? '';
    return ext === 'pdf' ? 'inline' : 'attachment';
  }

  private buildDownloadLink(bucket: string, objectName: string): string {
    const { linkConstructorForDownload } = accountingSheet.downloadFiles;
    const authToken = localStorage.getItem('authToken');
    if (authToken !== null) {
      return linkConstructorForDownload
        .replace('{authorizationToken}', authToken)
        .replace('{bucket}', bucket)
        .replace('{file}', objectName);
    }

    console.error('Authorization token is null, check local storage');
    return '';
  }

  /**
   * Legacy метод для AccountingSheet — не правит шаблоны AccountingSheetPage.
   * BC3.3 followup (rev.4): добавлены default disposition + target поля для TS strict;
   * AccountingSheet не использует target в template → DOM поведение неизменно.
   */
  public makeDocumentsLinks(
    object: Pretension | AccountingSheetInterface,
    options?: { excludeTypes?: string[] },
  ) {
    const excluded = new Set(options?.excludeTypes ?? []);
    this.documents = [];
    const items: DocumentLinkSource[] = object.documents;
    items.filter((item) => item.type !== null && !excluded.has(item.type)).forEach((item) => {
      this.documents.push({
        link: this.buildDownloadLink(item.bucket, item.objectName),
        type: this.getDocumentType(item.type),
        name: this.getNameFromS3ObjectName(item.objectName),
        disposition: 'attachment',
        target: null,
        // Вердикт сверки есть только у документов листа учёта; у претензий поля нет.
        compareResult: item.compareResult ?? null,
        objectName: item.objectName,
        documentType: item.type,
        rejectedAt: item.rejectedAt ?? null,
        rejectedBy: item.rejectedBy ?? null,
        rejectComment: item.rejectComment ?? null,
        acceptedAt: item.acceptedAt ?? null,
        acceptedBy: item.acceptedBy ?? null,
        compareDetails: item.compareDetails ?? null,
      });
    });
  }

  /**
   * BC3.3 followup (rev.4): sync URL builder. Backend стримит file content по GET
   * на /pretension-document-link?pretensionId=...&documentType=... через шлюз; токен — в куке.
   *
   * disposition determined frontend-side по extension для UI target=_blank решения;
   * backend имеет final authority через Content-Disposition response header.
   * Расхождение даёт degraded UX (пустая вкладка с download), не security issue.
   */
  public makeDocumentsLinksForPretension(pretension: Pretension): void {
    const items = pretension.documents.filter((d) => d.type !== null);
    this.documents = items.map((item) => {
      const disposition = this.inferDispositionFromObjectName(item.objectName);
      return {
        link: PretensionController.documentLink(pretension.id, item.type, item.objectName),
        type: this.getDocumentType(item.type),
        name: this.getNameFromS3ObjectName(item.objectName),
        disposition,
        target: disposition === 'inline' ? '_blank' : null,
        compareResult: null,
        objectName: item.objectName,
        documentType: item.type,
        rejectedAt: null,
        rejectedBy: null,
        rejectComment: null,
        acceptedAt: null,
        acceptedBy: null,
        compareDetails: null,
      };
    });
  }
}
